<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, provide, proxyRefs, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ImudeiDashboardPage from './ImudeiDashboardPage.vue';
import ImudeiDetailPage from './ImudeiDetailPage.vue';
import ImudeiFormPage from './ImudeiFormPage.vue';
import ImudeiProposalDashboard from './ImudeiProposalDashboard.vue';
import ImudeiProposalCreate from './ImudeiProposalCreate.vue';
import ImudeiProposalDetail from './ImudeiProposalDetail.vue';
import ImudeiProposalEdit from './ImudeiProposalEdit.vue';
import ImudeiVisitEdit from './ImudeiVisitEdit.vue';
import ImudeiVisitDetail from './ImudeiVisitDetail.vue';
import ImudeiVisitCreate from './ImudeiVisitCreate.vue';
import ImudeiVisitDashboardReference from './ImudeiVisitDashboardReference.vue';
import ImudeiFinanceCreate from './ImudeiFinanceCreate.vue';
import ImudeiFinanceEdit from './ImudeiFinanceEdit.vue';
import ImudeiFinanceDetail from './ImudeiFinanceDetail.vue';
import ImudeiFinanceDashboardReference from './ImudeiFinanceDashboardReference.vue';
import ImudeiBrokerCreateReference from './ImudeiBrokerCreateReference.vue';
import ImudeiBrokerDashboardReference from './ImudeiBrokerDashboardReference.vue';
import ImudeiBrokerEdit from './ImudeiBrokerEdit.vue';
import ImudeiBrokerProfileReference from './ImudeiBrokerProfileReference.vue';
import ImudeiCommissionDashboardReference from './ImudeiCommissionDashboardReference.vue';
import ImudeiCommissionCreateReference from './ImudeiCommissionCreateReference.vue';
import ImudeiCommissionDetail from './ImudeiCommissionDetail.vue';
import ImudeiCommissionEdit from './ImudeiCommissionEdit.vue';
import ImudeiPostSaleDashboardReference from './ImudeiPostSaleDashboardReference.vue';
import ImudeiPostSaleCreateReference from './ImudeiPostSaleCreateReference.vue';
import ImudeiPostSaleDetailReference from './ImudeiPostSaleDetailReference.vue';
import ImudeiPostSaleEdit from './ImudeiPostSaleEdit.vue';
import ImudeiReferenceTopbar from './ImudeiReferenceTopbar.vue';
import ImudeiReferenceSidebar from './ImudeiReferenceSidebar.vue';
import ImudeiOperationalPage from './ImudeiOperationalPage.vue';
import ImudeiSalesDashboard from './ImudeiSalesDashboard.vue';
import ImudeiSalesRuntimeWorkflow from './ImudeiSalesRuntimeWorkflow.vue';
import { useImudeiRealData } from './useImudeiRealData';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  module: { type: String, default: '' },
  mode: { type: String, default: '' },
});

const injectedRoute = useRoute();
const route =
  injectedRoute?.query && injectedRoute?.params
    ? injectedRoute
    : { name: '', query: {}, params: {} };
const router = useRouter();

const routeModule = Object.freeze({
  operacao_imobiliaria_vendas: 'sales',
  operacao_imobiliaria_vendas_detalhes: 'sales',
  operacao_imobiliaria_vendas_revisar: 'sales',
  operacao_imobiliaria_vendas_cadastro: 'sales',
  operacao_imobiliaria_vendas_nova: 'sales',
  lais_tenants_rent_flows: 'proposals',
  lais_tenants_rent_flows_status: 'proposals',
  lais_tenants_rent_flows_status_phase: 'proposals',
  visitas_index: 'visits',
  visita_show: 'visits',
  visita_edit: 'visits',
  visita_new: 'visits',
  operacao_imobiliaria_pos_venda: 'postSale',
  operacao_imobiliaria_pos_venda_detalhes: 'postSale',
  operacao_imobiliaria_pos_venda_status: 'postSale',
  operacao_imobiliaria_financeiro: 'finance',
  operacao_imobiliaria_financeiro_detalhes: 'finance',
  operacao_imobiliaria_financeiro_status: 'finance',
  operacao_imobiliaria_financeiro_status_alterar: 'finance',
  operacao_imobiliaria_corretor: 'brokers',
  financeiro_comissoes_index: 'commissions',
  operacao_imobiliaria_vendas_comissao: 'commissions',
});

const routeMode = Object.freeze({
  operacao_imobiliaria_vendas: 'dashboard',
  operacao_imobiliaria_vendas_detalhes: 'detail',
  operacao_imobiliaria_vendas_revisar: 'edit',
  operacao_imobiliaria_vendas_cadastro: 'edit',
  operacao_imobiliaria_vendas_nova: 'create',
  lais_tenants_rent_flows: 'dashboard',
  lais_tenants_rent_flows_status: 'detail',
  lais_tenants_rent_flows_status_phase: 'edit',
  visitas_index: 'dashboard',
  visita_show: 'detail',
  visita_edit: 'edit',
  visita_new: 'create',
  operacao_imobiliaria_pos_venda: 'dashboard',
  operacao_imobiliaria_pos_venda_detalhes: 'detail',
  operacao_imobiliaria_pos_venda_status: 'edit',
  operacao_imobiliaria_financeiro: 'dashboard',
  operacao_imobiliaria_financeiro_detalhes: 'detail',
  operacao_imobiliaria_financeiro_status: 'edit',
  operacao_imobiliaria_financeiro_status_alterar: 'edit',
  operacao_imobiliaria_corretor: 'dashboard',
  financeiro_index: 'dashboard',
  financeiro_comissoes_index: 'dashboard',
  operacao_imobiliaria_vendas_comissao: 'detail',
});

const routeModuleKey = currentRoute => {
  if (currentRoute.name === 'financeiro_index') {
    return currentRoute.query.tab === 'comissoes' ? 'commissions' : 'finance';
  }

  return routeModule[currentRoute.name] || 'sales';
};

const moduleKey = computed(
  () => props.module || routeModuleKey(route)
);
const selectedScreen = ref(
  props.mode || route.query.screen || routeMode[route.name] || 'dashboard'
);

watch(
  () => route.query.screen,
  nextScreen => {
    if (nextScreen) selectedScreen.value = nextScreen;
  }
);

watch(
  () => props.mode,
  nextMode => {
    if (nextMode) selectedScreen.value = nextMode;
  }
);

const screen = computed(() => props.mode || selectedScreen.value);
const isReferencePreview = computed(() => !route.name && Boolean(props.module));
const isProposalEdit = computed(
  () => moduleKey.value === 'proposals' && screen.value === 'edit'
);
const realData = useImudeiRealData({
  route,
  module: moduleKey,
  autoLoad: !isReferencePreview.value,
});
const { data, error, loading, refresh } = realData;
const runtimeModel = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: moduleKey,
    screen,
  })
);

provide('imudeiRealData', realData);
provide('imudeiReferencePreview', isReferencePreview);

const contentOffset = computed(() => {
  const offsets = {
    sales: 'pl-[178px]',
    proposals: 'pl-[54px]',
    visits: 'pl-[211px]',
    postSale: 'pl-[213px]',
    finance: 'pl-[213px]',
    brokers: screen.value === 'create' ? 'pl-[198px]' : 'pl-[98px]',
    commissions: 'pl-[183px]',
  };

  return offsets[moduleKey.value] || offsets.sales;
});

const navigate = destination => {
  if (!route.name) return;

  const target =
    typeof destination === 'string' ? { screen: destination } : destination;
  const destinationScreen = target.screen || 'dashboard';
  selectedScreen.value = destinationScreen;

  const query = { ...route.query };
  if (destinationScreen === 'dashboard') {
    delete query.screen;
  } else {
    query.screen = destinationScreen;
  }

  const params = { ...route.params };
  const routeTargetByModule = {
    sales: {
      create: { name: 'operacao_imobiliaria_vendas_nova' },
      detail: {
        name: 'operacao_imobiliaria_vendas_detalhes',
        recordParam: 'salesTransactionId',
      },
      edit: {
        name: 'operacao_imobiliaria_vendas_revisar',
        recordParam: 'salesTransactionId',
      },
    },
    visits: {
      create: { name: 'visita_new' },
      detail: { name: 'visita_show', recordParam: 'id' },
      edit: { name: 'visita_edit', recordParam: 'id' },
    },
    postSale: {
      detail: {
        name: 'operacao_imobiliaria_pos_venda_detalhes',
        recordParam: 'postSaleCaseId',
      },
      edit: {
        name: 'operacao_imobiliaria_pos_venda_status',
        recordParam: 'postSaleCaseId',
      },
    },
    finance: {
      detail: {
        name: 'operacao_imobiliaria_financeiro_detalhes',
        recordParam: 'salesTransactionId',
      },
      edit: {
        name: 'operacao_imobiliaria_financeiro_status',
        recordParam: 'salesTransactionId',
      },
    },
  };
  const routeTarget = routeTargetByModule[moduleKey.value]?.[destinationScreen];
  const recordParam = routeTarget?.recordParam;
  const queryIdByModule = {
    brokers: 'brokerId',
    commissions: 'commissionId',
    finance: 'financeId',
    postSale: 'postSaleCaseId',
    proposals: 'offerId',
    sales: 'saleId',
    visits: 'visitId',
  };

  if (target.id && recordParam) {
    params[recordParam] = target.id;
  }
  if (target.id && queryIdByModule[moduleKey.value]) {
    query[queryIdByModule[moduleKey.value]] = target.id;
  }

  router.push({
    name: routeTarget?.name || route.name,
    params,
    query,
  });
};
</script>

<template>
  <section
    data-imudei-parity-surface
    class="fixed inset-0 z-[9999] min-h-0 overflow-hidden bg-[#fcfbff] font-sans text-[#101958]"
  >
    <ImudeiReferenceSidebar :module="moduleKey" :screen="screen" />
    <ImudeiReferenceTopbar
      v-if="isProposalEdit"
      :module="moduleKey"
      :preview="isReferencePreview"
      :screen="screen"
    />
    <div
      class="relative h-full min-w-[1080px] overflow-y-auto"
      :class="[contentOffset, isProposalEdit ? 'pt-[83px]' : '']"
    >
      <ImudeiReferenceTopbar
        v-if="!isProposalEdit"
        :module="moduleKey"
        :preview="isReferencePreview"
        :screen="screen"
      />
      <div
        v-if="!isReferencePreview && loading"
        class="grid min-h-[calc(100%-56px)] place-items-center px-10"
      >
        <div
          class="w-full max-w-md rounded-3xl border border-[#e7e2ef] bg-white p-8 text-center shadow-[0_12px_40px_rgba(54,35,100,0.08)]"
        >
          <i
            class="i-lucide-loader-circle mx-auto size-8 animate-spin text-[#5a24c7]"
          />
          <h1 class="mt-4 text-lg font-bold">Carregando dados reais</h1>
          <p class="mt-2 text-sm text-[#66739b]">
            Atualizando informações da sua operação.
          </p>
        </div>
      </div>
      <div
        v-else-if="
          !isReferencePreview &&
          error &&
          screen === 'dashboard' &&
          !data?.records?.length
        "
        class="grid min-h-[calc(100%-56px)] place-items-center px-10"
      >
        <div
          class="w-full max-w-lg rounded-3xl border border-rose-100 bg-white p-8 text-center shadow-[0_12px_40px_rgba(54,35,100,0.08)]"
        >
          <i class="i-lucide-circle-alert mx-auto size-8 text-rose-500" />
          <h1 class="mt-4 text-lg font-bold">Não foi possível carregar</h1>
          <p class="mt-2 text-sm text-[#66739b]">{{ error }}</p>
          <button
            class="mt-6 rounded-xl bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-5 py-3 text-sm font-bold text-white"
            type="button"
            @click="refresh"
          >
            Tentar novamente
          </button>
        </div>
      </div>
      <ImudeiSalesDashboard
        v-else-if="!isReferencePreview && moduleKey === 'sales' && screen === 'dashboard'"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :history="runtimeModel.history"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiProposalDashboard
        v-else-if="!isReferencePreview && moduleKey === 'proposals' && screen === 'dashboard'"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :history="runtimeModel.history"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiVisitDashboardReference
        v-else-if="!isReferencePreview && moduleKey === 'visits' && screen === 'dashboard'"
        :agenda="data?.agenda || []"
        :history="runtimeModel.history"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiPostSaleDashboardReference
        v-else-if="!isReferencePreview && moduleKey === 'postSale' && screen === 'dashboard'"
        :history="runtimeModel.history"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiFinanceDashboardReference
        v-else-if="!isReferencePreview && moduleKey === 'finance' && screen === 'dashboard'"
        :history="runtimeModel.history"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiBrokerDashboardReference
        v-else-if="!isReferencePreview && moduleKey === 'brokers' && screen === 'dashboard'"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiCommissionDashboardReference
        v-else-if="!isReferencePreview && moduleKey === 'commissions' && screen === 'dashboard'"
        :history="runtimeModel.history"
        :metrics="runtimeModel.metrics"
        :records="runtimeModel.records"
        :total="data?.meta?.count || data?.meta?.total || runtimeModel.records.length"
        @navigate="navigate"
      />
      <ImudeiSalesRuntimeWorkflow
        v-else-if="!isReferencePreview && moduleKey === 'sales'"
        :screen="screen"
        @navigate="navigate"
      />
      <ImudeiProposalEdit
        v-else-if="!isReferencePreview && moduleKey === 'proposals' && screen === 'edit'"
        @navigate="navigate"
      />
      <ImudeiProposalDetail
        v-else-if="!isReferencePreview && moduleKey === 'proposals' && screen === 'detail'"
        @navigate="navigate"
      />
      <ImudeiProposalCreate
        v-else-if="!isReferencePreview && moduleKey === 'proposals' && screen === 'create'"
        @navigate="navigate"
      />
      <ImudeiVisitEdit
        v-else-if="!isReferencePreview && moduleKey === 'visits' && screen === 'edit'"
        @navigate="navigate"
      />
      <ImudeiVisitDetail
        v-else-if="!isReferencePreview && moduleKey === 'visits' && screen === 'detail'"
        @navigate="navigate"
      />
      <ImudeiVisitCreate
        v-else-if="!isReferencePreview && moduleKey === 'visits' && screen === 'create'"
        @navigate="navigate"
      />
      <ImudeiPostSaleEdit
        v-else-if="!isReferencePreview && moduleKey === 'postSale' && screen === 'edit'"
        @navigate="navigate"
      />
      <ImudeiPostSaleCreateReference
        v-else-if="!isReferencePreview && moduleKey === 'postSale' && screen === 'create'"
        @navigate="navigate"
      />
      <ImudeiFinanceCreate
        v-else-if="!isReferencePreview && moduleKey === 'finance' && screen === 'create'"
        @navigate="navigate"
      />
      <ImudeiFinanceDetail
        v-else-if="!isReferencePreview && moduleKey === 'finance' && screen === 'detail'"
      />
      <ImudeiFinanceEdit
        v-else-if="!isReferencePreview && moduleKey === 'finance' && screen === 'edit'"
        @navigate="navigate"
      />
      <ImudeiCommissionEdit
        v-else-if="!isReferencePreview && moduleKey === 'commissions' && screen === 'edit'"
        @navigate="navigate"
      />
      <ImudeiCommissionCreateReference
        v-else-if="!isReferencePreview && moduleKey === 'commissions' && screen === 'create'"
        @navigate="navigate"
      />
      <ImudeiCommissionDetail
        v-else-if="!isReferencePreview && moduleKey === 'commissions' && screen === 'detail'"
        @navigate="navigate"
      />
      <ImudeiPostSaleDetailReference
        v-else-if="!isReferencePreview && moduleKey === 'postSale' && screen === 'detail'"
        @navigate="navigate"
      />
      <ImudeiBrokerCreateReference
        v-else-if="!isReferencePreview && moduleKey === 'brokers' && screen === 'create'"
        @navigate="navigate"
      />
      <ImudeiBrokerProfileReference
        v-else-if="!isReferencePreview && moduleKey === 'brokers' && screen === 'detail'"
        @navigate="navigate"
      />
      <ImudeiBrokerEdit
        v-else-if="!isReferencePreview && moduleKey === 'brokers' && screen === 'edit'"
        @navigate="navigate"
      />
      <ImudeiOperationalPage
        v-else-if="!isReferencePreview"
        :module="moduleKey"
        :screen="screen"
        @navigate="navigate"
      />
      <template v-else>
        <ImudeiProposalDashboard
          v-if="moduleKey === 'proposals' && screen === 'dashboard'"
          preview
          @navigate="navigate"
        />
        <ImudeiFinanceDashboardReference
          v-else-if="moduleKey === 'finance' && screen === 'dashboard'"
          preview
          @navigate="navigate"
        />
        <ImudeiVisitDashboardReference
          v-else-if="moduleKey === 'visits' && screen === 'dashboard'"
          preview
          @navigate="navigate"
        />
        <ImudeiPostSaleDashboardReference
          v-else-if="moduleKey === 'postSale' && screen === 'dashboard'"
          preview
          @navigate="navigate"
        />
        <ImudeiBrokerDashboardReference
          v-else-if="moduleKey === 'brokers' && screen === 'dashboard'"
          preview
          @navigate="navigate"
        />
        <ImudeiCommissionDashboardReference
          v-else-if="moduleKey === 'commissions' && screen === 'dashboard'"
          preview
          @navigate="navigate"
        />
        <ImudeiDashboardPage
          v-else-if="screen === 'dashboard'"
          :module="moduleKey"
          @navigate="navigate"
        />
        <ImudeiProposalCreate
          v-else-if="moduleKey === 'proposals' && screen === 'create'"
          @navigate="navigate"
        />
        <ImudeiVisitCreate
          v-else-if="moduleKey === 'visits' && screen === 'create'"
          @navigate="navigate"
        />
        <ImudeiBrokerCreateReference
          v-else-if="moduleKey === 'brokers' && screen === 'create'"
          @navigate="navigate"
        />
        <ImudeiPostSaleCreateReference
          v-else-if="moduleKey === 'postSale' && screen === 'create'"
          @navigate="navigate"
        />
        <ImudeiCommissionCreateReference
          v-else-if="moduleKey === 'commissions' && screen === 'create'"
          @navigate="navigate"
        />
        <ImudeiPostSaleDetailReference
          v-else-if="moduleKey === 'postSale' && screen === 'detail'"
          @navigate="navigate"
        />
        <ImudeiDetailPage
          v-else-if="screen === 'detail'"
          :module="moduleKey"
          @navigate="navigate"
        />
        <ImudeiFormPage
          v-else
          :module="moduleKey"
          :screen="screen"
          @navigate="navigate"
        />
      </template>
    </div>
  </section>
</template>
